import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI, proposalsAPI, paymentsAPI, usersAPI } from '../services/api'
import ChatDialog from '../components/ChatDialog'
import {
  ArrowLeft, Calendar, MapPin, Euro, Building,
  Clock, ChevronRight, User, Star,
  Package, AlertCircle, CheckCircle,
  X, Eye, MessageSquare, ThumbsUp, Briefcase, Play, ShieldCheck,
  Instagram, Youtube, Video, Users, RotateCcw, CreditCard, LayoutDashboard
} from 'lucide-react'
import { getPortfolioEmbed } from '../utils/portfolioEmbed'
import PortfolioVideo from '../components/PortfolioVideo'

// TikTok zit niet in lucide — eigen mini-SVG in dezelfde stijl
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
)

// Try to derive a preview image / video source from a portfolio item.
// Returns { kind: 'image' | 'video' | 'link' | 'none', src, title?, host? }
const getPortfolioPreview = (item) => {
  if (!item) return { kind: 'none' }
  if (item.thumbnailUrl) return { kind: 'image', src: item.thumbnailUrl }
  const url = item.videoUrl || ''
  if (!url) return { kind: 'none' }
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,15})/)
  if (yt) return { kind: 'image', src: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` }
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(url)) return { kind: 'video', src: url }
  // Onze eigen CDN serveert /uploads/videos zonder extensie
  if (/\/uploads\/videos\//i.test(url)) return { kind: 'video', src: url }
  // Anders: gewone webpagina-link
  let host = ''
  try { host = new URL(url).hostname.replace(/^www\./, '') } catch (_) {}
  return { kind: 'link', src: url, title: item.title, host }
}

// Door de creator geboden bedrag. finalPrice valt in de backend terug op 0 wanneer
// er geen custom prijs is (negotiable), dus 0 telt hier als "geen bod".
const getProposalAmount = (proposal) => {
  const amount = proposal.customPricing?.amount
    || (proposal.finalPrice > 0 ? proposal.finalPrice : 0)
    || proposal.customPricing?.total
  return amount && amount > 0 ? amount : null
}

const formatFollowers = (n) => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'K' : String(n)

// Status van het (kind)project van een gekozen creator — elke creator heeft na
// acceptatie een eigen project met eigen aanbetaling en workspace.
const CHILD_STATUS_LABEL = {
  proposal_accepted: 'Wacht op aanbetaling',
  awaiting_deposit: 'Wacht op aanbetaling',
  creator_selected: 'Creator gekozen',
  awaiting_product_delivery: 'Wacht op product',
  in_progress: 'In productie',
  delivered: 'Opgeleverd',
  under_review: 'In beoordeling',
  revision_requested: 'Revisie gevraagd',
  approved: 'Goedgekeurd — eindbetaling',
  completed: 'Afgerond',
  cancelled: 'Geannuleerd',
  refunded: 'Terugbetaald',
  disputed: 'In geschil'
}

// Statussen waarin een opdracht géén voorstellen meer kan accepteren
const NO_ACCEPT_STATUSES = ['draft', 'cancelled', 'completed', 'refunded', 'disputed']

const AVAILABILITY_LABEL = {
  'immediately': 'Direct beschikbaar',
  'within-week': 'Binnen 1 week beschikbaar',
  'within-month': 'Binnen 1 maand beschikbaar',
  'not-available': 'Momenteel niet beschikbaar'
}

// Levertijd in dagen: expliciet veld → afgeleid uit deliveryDate → null (niet opgegeven).
const getDeliveryDays = (proposal) => {
  if (proposal.deliveryTimeInDays) return proposal.deliveryTimeInDays
  const d = proposal.proposedTimeline?.deliveryDate
  if (d) {
    const days = Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24))
    return days > 0 ? days : null
  }
  return null
}

const ProjectProposalsPage = ({ projectId: propProjectId, onNavigate, onBack } = {}) => {
  const params = useParams()
  const id = propProjectId || params.id
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [proposals, setProposals] = useState([])
  const [projectInfo, setProjectInfo] = useState(null)
  const [undoingId, setUndoingId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedProposal, setSelectedProposal] = useState(null)
  const [showPortfolioModal, setShowPortfolioModal] = useState(false)
  const [selectedCreatorPortfolio, setSelectedCreatorPortfolio] = useState(null)
  const [portfolioCache, setPortfolioCache] = useState({})
  const [showChatDialog, setShowChatDialog] = useState(false)
  const [selectedChatCreator, setSelectedChatCreator] = useState(null)
  const [acceptProposal, setAcceptProposal] = useState(null)
  const [accepting, setAccepting] = useState(false)
  const [sortBy, setSortBy] = useState('newest')
  const [expandedIds, setExpandedIds] = useState(() => new Set())

  const toggleExpanded = (proposalId) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      next.has(proposalId) ? next.delete(proposalId) : next.add(proposalId)
      return next
    })
  }

  // Client-side sortering. Voorstellen zonder prijs/levertijd zakken naar onderen.
  const sortedProposals = React.useMemo(() => {
    const list = [...proposals]
    switch (sortBy) {
      case 'price-asc':
        return list.sort((a, b) => (getProposalAmount(a) ?? Infinity) - (getProposalAmount(b) ?? Infinity))
      case 'price-desc':
        return list.sort((a, b) => (getProposalAmount(b) ?? -Infinity) - (getProposalAmount(a) ?? -Infinity))
      case 'delivery':
        return list.sort((a, b) => (getDeliveryDays(a) ?? Infinity) - (getDeliveryDays(b) ?? Infinity))
      case 'newest':
      default:
        return list.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
    }
  }, [proposals, sortBy])

  // Body-scroll-lock zolang een modal open is (standalone); embedded vangt
  // `overscroll-contain` op de overlay het meescrollen van #dashboard-scroll op.
  useEffect(() => {
    if (!showPortfolioModal && !acceptProposal) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [showPortfolioModal, acceptProposal])

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'bedrijf') {
      onNavigate ? onNavigate('home') : navigate('/dashboard')
      return
    }

    fetchProjectAndProposals()
  }, [id, isAuthenticated, user])

  const fetchProjectAndProposals = async () => {
    try {
      setLoading(true)

      // Fetch project details
      const projectResponse = await projectsAPI.getProject(id)
      console.log('Project response:', projectResponse.data)
      const projectData = projectResponse.data.project || projectResponse.data
      console.log('Setting project data:', projectData)
      setProject(projectData)

      // Fetch proposals for this project
      const proposalsResponse = await proposalsAPI.getProjectProposals(id)
      console.log('Proposals response:', proposalsResponse.data)
      setProposals(proposalsResponse.data.proposals || [])
      setProjectInfo(proposalsResponse.data.projectInfo || null)

    } catch (error) {
      console.error('Error fetching data:', error)
      setError('Kon gegevens niet laden')
    } finally {
      setLoading(false)
    }
  }

  const [extending, setExtending] = useState(false)

  // Verlopen brief heropenen met gekozen aantal dagen; voorstellen blijven staan.
  const handleVerleng = async (dagen) => {
    if (extending) return
    setExtending(true)
    try {
      await projectsAPI.verlengProject(id, dagen)
      await fetchProjectAndProposals()
    } catch (error) {
      console.error('Error extending brief:', error)
      alert(error.response?.data?.message || 'Kon de brief niet verlengen')
    } finally {
      setExtending(false)
    }
  }

  // Opent de bevestigingsmodal; de daadwerkelijke acceptance gebeurt in confirmAcceptProposal.
  const handleAcceptProposal = (proposal) => {
    setAcceptProposal(proposal)
  }

  const confirmAcceptProposal = async () => {
    if (!acceptProposal || accepting) return
    const proposalId = acceptProposal.id
    setAccepting(true)
    try {
      // Stap 1: accepteer voorstel — backend maakt een KINDPROJECT voor deze creator
      // (eigen aanbetaling/workspace, 72u timer) en geeft dat id terug als project.id
      const acceptResp = await proposalsAPI.acceptProposal(proposalId)
      const projectId = acceptResp?.data?.project?.id || project?.id || id

      // Stap 2: maak Stripe Checkout sessie voor de aanbetaling
      const checkoutResp = await paymentsAPI.processDepositPayment(projectId)
      const checkoutUrl = checkoutResp?.data?.data?.checkoutUrl || checkoutResp?.data?.checkoutUrl

      if (checkoutUrl) {
        window.location.href = checkoutUrl
        return
      }

      // Fallback als checkout niet beschikbaar
      alert('Voorstel geaccepteerd. Ga naar je opdracht om de aanbetaling te voldoen.')
      setAcceptProposal(null)
      fetchProjectAndProposals()
    } catch (error) {
      console.error('Error accepting proposal:', error)
      const msg = error?.response?.data?.message || error?.response?.data?.error || 'Er is een fout opgetreden bij het accepteren van het voorstel'
      alert(msg)
    } finally {
      setAccepting(false)
    }
  }

  // Meerdere creators per opdracht: elk geaccepteerd voorstel heeft een eigen
  // kindproject (childProject) met eigen aanbetaling en workspace. Opdrachten van
  // vóór deze wijziging hebben de creator nog direct op het project zelf staan.
  const getAcceptedTarget = (proposal) => {
    const child = proposal.childProject
    if (child?.id) {
      const depositPaid = child.payment?.deposit?.status === 'paid'
      return {
        id: child.id,
        status: child.status,
        depositPaid,
        canUndo: !depositPaid && child.status === 'proposal_accepted',
        isChild: true
      }
    }
    const selectedId = project?.selectedCreator?.id || project?.selectedCreator
    if (selectedId && String(selectedId) === String(proposal.creator?.id)) {
      const depositPaid = project.payment?.deposit?.status === 'paid'
      return {
        id: project.id,
        status: project.status,
        depositPaid,
        canUndo: !depositPaid && project.status === 'proposal_accepted',
        isChild: false
      }
    }
    return null
  }

  const openWorkspace = (targetId) => {
    if (onNavigate) onNavigate('project-workspace', targetId)
    else navigate(`/project-workspace/${targetId}`)
  }

  const goToPayment = (targetId) => navigate(`/projects/${targetId}/payment`)

  // Reservering terugdraaien zolang de aanbetaling niet betaald is: het voorstel
  // komt weer op 'Nieuw' te staan en de creator krijgt bericht.
  const handleUndoAccept = async (targetId) => {
    if (undoingId) return
    if (!confirm('Reservering terugdraaien? Het voorstel komt dan weer open te staan.')) return
    setUndoingId(targetId)
    try {
      await projectsAPI.undoAcceptProposal(targetId)
      await fetchProjectAndProposals()
    } catch (error) {
      console.error('Error undoing accept:', error)
      alert(error?.response?.data?.message || 'Terugdraaien is niet gelukt')
    } finally {
      setUndoingId(null)
    }
  }

  const acceptedProposals = proposals.filter(p => p.status === 'accepted')
  const canAccept = projectInfo?.canAccept ?? (project ? !NO_ACCEPT_STATUSES.includes(project.status) : false)

  const handleRejectProposal = async (proposalId) => {
    const reason = prompt('Waarom wijs je dit voorstel af? (optioneel)')
    try {
      await proposalsAPI.rejectProposal(proposalId, { reason: reason || '' })
      alert('Voorstel afgewezen')
      fetchProjectAndProposals() // Refresh data
    } catch (error) {
      console.error('Error rejecting proposal:', error)
      alert('Er is een fout opgetreden bij het afwijzen van het voorstel')
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price)
  }

  // Biedingen worden excl. BTW opgeslagen, terwijl het brief-budget incl. BTW
  // wordt getoond. Die twee naast elkaar leggen zonder om te rekenen laat een
  // bod van €350 lager lijken dan een budget van €363, terwijl het in
  // werkelijkheid €423,50 is — 17% eróver. Daarom hier altijd incl. als kop.
  const formatPriceIncl = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price)
  }

  const BTW = 1.21

  const handleOpenChat = (creator) => {
    setSelectedChatCreator(creator)
    setShowChatDialog(true)
  }

  const handleViewPortfolio = async (creatorId) => {
    try {
      // Check cache first
      if (portfolioCache[creatorId]) {
        console.log('Using cached portfolio for creator:', creatorId)
        setSelectedCreatorPortfolio(portfolioCache[creatorId])
        setShowPortfolioModal(true)
        return
      }

      console.log('Fetching portfolio for creator:', creatorId)
      const response = await usersAPI.getCreator(creatorId)
      console.log('Creator API response:', response.data)
      const creatorData = response.data.creator || response.data
      console.log('Creator data extracted:', creatorData)

      // Cache the result
      setPortfolioCache(prev => ({
        ...prev,
        [creatorId]: creatorData
      }))

      setSelectedCreatorPortfolio(creatorData)
      setShowPortfolioModal(true)
    } catch (error) {
      console.error('Error fetching creator portfolio:', error)
      if (error.response?.status === 429) {
        alert('Te veel verzoeken. Probeer het over een moment opnieuw.')
      } else {
        alert('Kon portfolio niet laden')
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1D8BF8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Voorstellen laden...</p>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Kon gegevens niet laden</h2>
          <p className="text-[#475569] mb-6">{error}</p>
          {onNavigate ? (
            <button onClick={() => onNavigate('home')} className="text-[#1D8BF8] hover:underline">
              Terug naar dashboard
            </button>
          ) : (
            <Link to="/dashboard" className="text-[#1D8BF8] hover:underline">
              Terug naar dashboard
            </Link>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Header - only show in standalone mode */}
      {!onNavigate && (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
              UGC<span className="text-[#1D8BF8]">4you</span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/projects" className="hover:text-[#1D8BF8] transition">Opdrachten</Link>
              <Link to="/creators" className="hover:text-[#1D8BF8] transition">Creators</Link>
              <Link to="/dashboard" className="px-4 py-2 bg-[#3EB489] hover:bg-[#359E78] rounded-lg transition text-white">
                Dashboard
              </Link>
            </nav>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 ${onNavigate ? 'pt-6' : 'pt-20 md:pt-32'} pb-20`}>
        {/* Back Button — 44 px tikdoel zonder de layout te verschuiven */}
        {onBack ? (
          <button onClick={onBack} className="inline-flex items-center min-h-[44px] -mt-2.5 mb-6 text-[#475569] hover:text-[#1D8BF8] transition">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar dashboard
          </button>
        ) : (
          <Link to="/dashboard" className="inline-flex items-center min-h-[44px] -mt-2.5 mb-6 text-[#475569] hover:text-[#1D8BF8] transition">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar dashboard
          </Link>
        )}

        {/* Verlopen brief: verleng-banner (voorstellen blijven 14 dagen staan) */}
        {project.status === 'cancelled' && (
          <div className="mb-6 p-5 md:p-6 rounded-2xl bg-white border border-[#F59E0B]/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[#0F172A]">Deze brief is verlopen</p>
                <p className="text-sm text-[#475569] mt-1.5 leading-relaxed">
                  Je ontvangen voorstellen blijven nog 14 dagen bewaard. Zet de brief weer open om een creator te kiezen — nieuwe creators kunnen dan ook weer reageren.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {[7, 14, 30].map(dagen => (
                    <button
                      key={dagen}
                      onClick={() => handleVerleng(dagen)}
                      disabled={extending}
                      className="px-4 py-2.5 rounded-xl bg-[#3EB489] hover:bg-[#359E78] disabled:opacity-50 text-white font-semibold text-sm transition"
                    >
                      + {dagen} dagen
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Header */}
        <div className="relative mb-8">
          <div className="relative p-5 md:p-8 rounded-3xl bg-white border border-gray-200 shadow-md">
            <div className="flex flex-col sm:flex-row sm:justify-between items-start mb-4 gap-2">
              <h1 className="text-2xl md:text-3xl font-bold break-words min-w-0">{project.title}</h1>
              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-sm whitespace-nowrap flex-shrink-0">
                {proposals.length} voorstellen
                {acceptedProposals.length > 0 && ` (${acceptedProposals.length} geaccepteerd)`}
              </span>
            </div>

            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2 text-[#475569]">
                <Euro className="w-4 h-4" />
                <span>{project.budget ? `${formatPrice(project.budget * 1.21)} incl. BTW` : 'Budget niet beschikbaar'}</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]">
                <Calendar className="w-4 h-4" />
                <span>Deadline: {project.deadline ? new Date(project.deadline).toLocaleDateString('nl-NL') : 'Deadline niet beschikbaar'}</span>
              </div>
              <div className="flex items-center gap-2 text-[#475569]">
                <Eye className="w-4 h-4" />
                <span>{project.stats?.views || 0} views</span>
              </div>
            </div>
          </div>
        </div>

        {/* Proposals List */}
        {proposals.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[#475569]/50" />
            <h3 className="text-2xl font-bold mb-2">Nog geen voorstellen</h3>
            <p className="text-[#475569] mb-8 max-w-md mx-auto">
              Er zijn nog geen voorstellen ingediend voor dit project. Zodra creators reageren, zie je ze hier.
            </p>
            {onNavigate ? (
              <button onClick={() => onNavigate('project-detail', id)} className="text-[#1D8BF8] hover:underline">
                Bekijk project details
              </button>
            ) : (
              <Link to={`/projects/${id}`} className="text-[#1D8BF8] hover:underline">
                Bekijk project details
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Gekozen creators — elk met eigen aanbetaling en workspace */}
            {acceptedProposals.length > 0 && (
              <div className="rounded-2xl bg-white border border-[#3EB489]/40 p-5 md:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <h2 className="text-lg md:text-xl font-bold text-[#0F172A] inline-flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#3EB489]" />
                    Gekozen creators ({acceptedProposals.length})
                  </h2>
                  {canAccept && (
                    <span className="text-xs text-[#475569]">Je kunt hieronder nog meer creators kiezen</span>
                  )}
                </div>
                <p className="text-sm text-[#475569] mb-4">Elke creator heeft een eigen workspace en eigen aanbetaling op basis van zijn of haar voorstel.</p>
                <ul className="divide-y divide-gray-100">
                  {acceptedProposals.map(p => {
                    const t = getAcceptedTarget(p)
                    const amount = getProposalAmount(p)
                    const initial = (p.creator?.name || 'U').charAt(0).toUpperCase()
                    return (
                      <li key={p.id} className="flex flex-wrap items-center gap-3 md:gap-4 py-4">
                        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-gray-200" style={{ background: p.creator?.profilePictureUrl ? '#F1F5F9' : '#3EB489', display: 'grid', placeItems: 'center' }}>
                          {p.creator?.profilePictureUrl ? (
                            <img src={p.creator.profilePictureUrl} alt={p.creator.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-bold">{initial}</span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#0F172A] truncate">{p.creator?.name}</p>
                          <p className="text-xs text-[#475569] mt-0.5">
                            {t ? (CHILD_STATUS_LABEL[t.status] || t.status) : 'Geaccepteerd'}
                            {amount ? ` · ${formatPrice(amount)} excl. BTW` : ''}
                          </p>
                        </div>
                        {t && (
                          <div className="flex items-center gap-2 flex-wrap basis-full sm:basis-auto pl-[52px] sm:pl-0">
                            {t.depositPaid ? (
                              <button
                                onClick={() => openWorkspace(t.id)}
                                className="px-4 py-2 min-h-[44px] sm:min-h-0 rounded-xl bg-[#1D8BF8] hover:bg-[#1670c9] text-white text-sm font-semibold transition inline-flex items-center gap-1.5 whitespace-nowrap"
                              >
                                <LayoutDashboard className="w-4 h-4" />
                                Workspace openen
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => goToPayment(t.id)}
                                  className="px-4 py-2 min-h-[44px] sm:min-h-0 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white text-sm font-semibold transition inline-flex items-center gap-1.5 whitespace-nowrap"
                                >
                                  <CreditCard className="w-4 h-4" />
                                  Aanbetaling betalen
                                </button>
                                {t.canUndo && (
                                  <button
                                    onClick={() => handleUndoAccept(t.id)}
                                    disabled={undoingId === t.id}
                                    className="px-3 py-2 rounded-xl border border-gray-200 text-[#475569] hover:border-red-300 hover:text-red-500 text-sm font-medium transition inline-flex items-center gap-1.5 disabled:opacity-50"
                                    title="Reservering terugdraaien"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                    {undoingId === t.id ? 'Bezig…' : 'Terugdraaien'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
              <h2 className="text-xl md:text-2xl font-bold">Ontvangen Voorstellen ({proposals.length})</h2>

              {/* Sorteren — helpt bedrijven 10-15 voorstellen snel te vergelijken */}
              <div className="flex items-center gap-2 flex-nowrap overflow-x-auto scrollbar-hide -mx-4 px-4 w-[calc(100%+2rem)] sm:w-auto sm:flex-wrap sm:overflow-visible sm:mx-0 sm:px-0">
                <span className="text-sm text-[#475569] mr-1 whitespace-nowrap flex-shrink-0">Sorteer op:</span>
                {[
                  { key: 'newest', label: 'Nieuwste eerst' },
                  { key: 'price-asc', label: 'Prijs: laag → hoog' },
                  { key: 'price-desc', label: 'Prijs: hoog → laag' },
                  { key: 'delivery', label: 'Snelste levering' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-2 sm:py-1.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition border ${
                      sortBy === opt.key
                        ? 'bg-[#1D8BF8] text-white border-[#1D8BF8]'
                        : 'bg-white text-[#475569] border-gray-200 hover:border-[#1D8BF8]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Rijtjes van 3 op grote schermen zodat voorstellen naast elkaar te vergelijken zijn */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {sortedProposals.map((proposal) => {
              // Door de creator geboden bedrag (0/undefined = geen bod → "Prijs in overleg")
              const amount = getProposalAmount(proposal)
              const isNegotiable = proposal.customPricing?.negotiable === true
              const deliveryDays = getDeliveryDays(proposal)
              const isExpanded = expandedIds.has(proposal.id)
              const hasLongText = (proposal.motivation && proposal.motivation.length > 100) || !!proposal.approach
              const baseRate = proposal.creator.pricing?.baseRate
              const stats = proposal.creator.stats || {}
              const sm = proposal.creator.socialMedia || {}
              const platforms = [
                { label: 'TikTok', Icon: TikTokIcon, followers: sm.tiktok?.followers || 0 },
                { label: 'Instagram', Icon: Instagram, followers: sm.instagram?.followers || 0 },
                { label: 'YouTube', Icon: Youtube, followers: sm.youtube?.followers || 0 }
              ].filter(p => p.followers > 0)
              const availabilityLabel = AVAILABILITY_LABEL[proposal.creator.availability]
              const initial = (proposal.creator.name || 'U').charAt(0).toUpperCase()
              const goToProfile = () => onNavigate ? onNavigate('creator-detail', proposal.creator.id) : navigate(`/creators/${proposal.creator.id}`)
              const portfolioItems = proposal.creator.portfolio || []
              const extraPortfolio = portfolioItems.length - 3
              return (
              <div key={proposal.id} className="group relative h-full">
                <div className={`relative h-full flex flex-col rounded-2xl bg-white border transition-all duration-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_16px_40px_-20px_rgba(15,23,42,0.25)] ${
                  proposal.status === 'accepted' ? 'border-[#3EB489]' :
                  proposal.status === 'rejected' ? 'border-gray-200 opacity-70' :
                  'border-gray-200 hover:border-[#1D8BF8]/40'
                }`}>

                  {/* ===== Identiteit ===== */}
                  <div className="p-5 md:p-6">
                    <div className="flex items-start gap-4">
                      <button
                        onClick={goToProfile}
                        title="Bekijk profiel"
                        className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 ring-1 ring-gray-200 transition group-hover:ring-[#1D8BF8]/40"
                        style={{ background: proposal.creator.profilePictureUrl ? '#F1F5F9' : '#3EB489', display: 'grid', placeItems: 'center' }}
                      >
                        {proposal.creator.profilePictureUrl ? (
                          <img src={proposal.creator.profilePictureUrl} alt={proposal.creator.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white text-xl font-bold">{initial}</span>
                        )}
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <button onClick={goToProfile} className="text-base md:text-lg font-bold truncate text-left leading-tight hover:text-[#1D8BF8] transition" title="Bekijk profiel">
                            {proposal.creator.name}
                          </button>
                          <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                            proposal.status === 'accepted' ? 'border-[#3EB489]/40 text-[#3EB489]' :
                            proposal.status === 'rejected' ? 'border-gray-200 text-[#94A3B8]' :
                            'border-[#1D8BF8]/30 text-[#1D8BF8]'
                          }`}>
                            {proposal.status === 'submitted' ? 'Nieuw' :
                             proposal.status === 'accepted' ? 'Geaccepteerd' :
                             proposal.status === 'withdrawn' ? 'Vervallen' : 'Afgewezen'}
                          </span>
                        </div>

                        {proposal.creator.location?.city && (
                          <div className="flex items-center gap-1 mt-1.5 text-xs text-[#94A3B8]">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{proposal.creator.location.city}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-[#475569] text-sm leading-relaxed line-clamp-2 mt-4">
                      {proposal.creator.bio || 'Geen bio beschikbaar'}
                    </p>

                    {/* Bereik + kerncijfers */}
                    {(platforms.length > 0 || baseRate || stats.completedProjects > 0 || stats.averageRating > 0) && (
                      <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                        {platforms.map(p => (
                          <span key={p.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                            <p.Icon className="w-3.5 h-3.5 text-[#94A3B8]" />
                            <strong className="text-[#0F172A] font-semibold">{formatFollowers(p.followers)}</strong>
                          </span>
                        ))}
                        {stats.averageRating > 0 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                            <Star className="w-3.5 h-3.5 text-[#F59E0B]" fill="currentColor" />
                            <strong className="text-[#0F172A] font-semibold">{stats.averageRating.toFixed(1)}</strong>
                          </span>
                        )}
                        {stats.completedProjects > 0 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                            <strong className="text-[#0F172A] font-semibold">{stats.completedProjects}</strong> voltooid
                          </span>
                        )}
                        {baseRate && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569]">
                            vanaf <strong className="text-[#0F172A] font-semibold">€{baseRate}</strong>
                          </span>
                        )}
                      </div>
                    )}

                    {availabilityLabel && (
                      <div className="flex items-center gap-1.5 mt-3 text-xs font-medium text-[#3EB489]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3EB489]" />
                        {availabilityLabel}
                      </div>
                    )}
                  </div>

                  {/* ===== Bod: het cijfer waarop bedrijven vergelijken ===== */}
                  <div className="px-5 md:px-6 py-4 border-y border-gray-100 flex items-end justify-between gap-3">
                    {amount ? (
                      <div className="min-w-0">
                        <div className="text-2xl font-black leading-none tracking-tight tabular-nums text-[#0F172A]">
                          {formatPriceIncl(amount * BTW)}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] mt-2">
                          incl. BTW · {formatPrice(amount)} excl.
                          {isNegotiable && ' · onderhandelbaar'}
                        </div>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <div className="text-lg font-bold leading-none text-[#475569]">Prijs in overleg</div>
                        <div className="text-[11px] text-[#94A3B8] mt-2">Creator bespreekt de prijs graag met je</div>
                      </div>
                    )}
                    <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#475569] whitespace-nowrap">
                      <Clock className="w-3.5 h-3.5 text-[#94A3B8]" />
                      {deliveryDays ? `${deliveryDays} dgn levertijd` : 'Levertijd n.t.b.'}
                    </span>
                  </div>

                  {/* ===== Inhoud van het voorstel ===== */}
                  <div className="p-5 md:p-6 space-y-6">
                    {/* Motivatie + aanpak — standaard ingeklapt tot 3 regels zodat kaarten scanbaar blijven */}
                    <div>
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Motivatie</h4>
                      <div className="pl-3.5 border-l-2 border-[#1D8BF8]/25">
                        <p className={`text-[#0F172A] leading-relaxed text-sm break-words [overflow-wrap:anywhere] ${isExpanded ? '' : 'line-clamp-3'}`}>
                          {proposal.motivation}
                        </p>

                        {isExpanded && proposal.approach && (
                          <div className="mt-5">
                            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Aanpak</h4>
                            <p className="text-[#0F172A] leading-relaxed text-sm break-words [overflow-wrap:anywhere]">
                              {proposal.approach}
                            </p>
                          </div>
                        )}
                      </div>

                      {hasLongText && (
                        <button
                          onClick={() => toggleExpanded(proposal.id)}
                          className="text-[#1D8BF8] text-sm font-medium mt-3 hover:underline"
                        >
                          {isExpanded ? 'Toon minder' : 'Lees meer'}
                        </button>
                      )}
                    </div>

                    {/* Portfolio thumbnails (3 max) — matches CreatorsPage cards */}
                    {portfolioItems.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">
                            Portfolio ({portfolioItems.length})
                          </h4>
                          <button
                            onClick={() => handleViewPortfolio(proposal.creator.id)}
                            className="text-[11px] font-medium text-[#1D8BF8] hover:underline"
                          >
                            Bekijk alles
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {portfolioItems.slice(0, 3).map((item, i) => {
                            const preview = getPortfolioPreview(item)
                            return (
                              <div
                                key={i}
                                className="relative aspect-[4/5] rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200/70 group/thumb cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); handleViewPortfolio(proposal.creator.id) }}
                              >
                                {preview.kind === 'image' ? (
                                  <img src={preview.src} alt={item.title || 'Portfolio'} className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                                ) : preview.kind === 'video' ? (
                                  <video src={preview.src} muted playsInline preload="none" className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105" onMouseEnter={(e) => { e.currentTarget.play().catch(() => {}) }} onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0 }} />
                                ) : preview.kind === 'link' ? (
                                  /* Geen uitgaande <a> meer: dit opende de externe bron in
                                     een nieuw tabblad. Alleen een label; de video zelf is
                                     te zien in de portfolio-modal op het platform. */
                                  <div
                                    title={preview.title}
                                    className="w-full h-full bg-gradient-to-br from-[#3EB489] to-[#2f8f6d] flex flex-col items-center justify-center text-white p-2 text-center"
                                  >
                                    <svg viewBox="0 0 24 24" className="w-5 h-5 mb-1.5" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                    <div className="text-[10px] font-bold leading-tight line-clamp-2">{preview.title || 'Video'}</div>
                                  </div>
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300" />
                                )}
                                {preview.kind !== 'link' && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md transition-transform duration-200 group-hover/thumb:scale-110">
                                      <Play className="w-3.5 h-3.5 text-[#0F172A] ml-0.5" fill="currentColor" />
                                    </div>
                                  </div>
                                )}
                                {i === 2 && extraPortfolio > 0 && (
                                  <div className="absolute inset-0 bg-[#0F172A]/60 flex items-center justify-center pointer-events-none">
                                    <span className="text-white text-sm font-bold">+{extraPortfolio}</span>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Specialisaties */}
                    {proposal.creator.niches && proposal.creator.niches.length > 0 && (
                      <div>
                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-2.5">Specialisaties</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {proposal.creator.niches.slice(0, 3).map((niche, index) => (
                            <span key={index} className="px-2.5 py-1 rounded-lg border border-gray-200 text-[11px] text-[#475569] capitalize">
                              {niche}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Acties — mt-auto drukt ze tegen de onderkant zodat alle kaarten gelijk eindigen */}
                  <div className="mt-auto px-5 md:px-6 pb-5 md:pb-6 pt-5 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mb-4">
                      <Calendar className="w-3 h-3" />
                      Ingediend op {new Date(proposal.submittedAt).toLocaleDateString('nl-NL')}
                    </div>

                    {proposal.status === 'submitted' && canAccept && (
                      <button
                        onClick={() => handleAcceptProposal(proposal)}
                        className="w-full px-5 py-3.5 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2 shadow-sm shadow-[#3EB489]/20"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Accepteer voorstel
                      </button>
                    )}

                    {/* Geaccepteerd: eigen aanbetaling / workspace van deze creator */}
                    {proposal.status === 'accepted' && (() => {
                      const t = getAcceptedTarget(proposal)
                      if (!t) return null
                      return (
                        <div className="space-y-2 mb-2">
                          <p className="text-xs text-[#475569]">{CHILD_STATUS_LABEL[t.status] || t.status}</p>
                          {t.depositPaid ? (
                            <button
                              onClick={() => openWorkspace(t.id)}
                              className="w-full px-5 py-3.5 rounded-xl bg-[#1D8BF8] hover:bg-[#1670c9] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              Workspace openen
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => goToPayment(t.id)}
                                className="w-full px-5 py-3.5 rounded-xl bg-[#3EB489] hover:bg-[#359E78] text-white font-semibold text-sm transition inline-flex items-center justify-center gap-2 shadow-sm shadow-[#3EB489]/20"
                              >
                                <CreditCard className="w-4 h-4" />
                                Aanbetaling betalen
                              </button>
                              {t.canUndo && (
                                <button
                                  onClick={() => handleUndoAccept(t.id)}
                                  disabled={undoingId === t.id}
                                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-[#475569] hover:border-red-300 hover:text-red-500 transition inline-flex items-center justify-center gap-1.5 text-xs font-medium disabled:opacity-50"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  {undoingId === t.id ? 'Bezig…' : 'Terugdraaien'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )
                    })()}

                    <div className={`grid gap-2 ${proposal.status === 'submitted' && canAccept ? 'grid-cols-2 sm:grid-cols-3 mt-2' : 'grid-cols-2'}`}>
                      <button
                        onClick={() => handleViewPortfolio(proposal.creator.id)}
                        className="px-3 py-2.5 rounded-xl border border-gray-200 text-[#475569] hover:border-[#1D8BF8] hover:text-[#1D8BF8] transition inline-flex items-center justify-center gap-1.5 text-xs font-medium"
                        title="Bekijk portfolio"
                      >
                        <Briefcase className="w-3.5 h-3.5" />
                        Portfolio
                      </button>

                      <button
                        onClick={goToProfile}
                        className="px-3 py-2.5 rounded-xl border border-gray-200 text-[#475569] hover:border-[#1D8BF8] hover:text-[#1D8BF8] transition inline-flex items-center justify-center gap-1.5 text-xs font-medium"
                        title="Bekijk profiel"
                      >
                        <User className="w-3.5 h-3.5" />
                        Profiel
                      </button>

                      {proposal.status === 'submitted' && canAccept && (
                        <button
                          onClick={() => handleRejectProposal(proposal.id)}
                          className="col-span-2 sm:col-span-1 px-3 py-2.5 rounded-xl border border-gray-200 text-[#94A3B8] hover:border-red-300 hover:text-red-500 transition inline-flex items-center justify-center gap-1.5 text-xs font-medium"
                          title="Voorstel afwijzen"
                        >
                          <X className="w-3.5 h-3.5" />
                          Afwijzen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )
            })}
            </div>
          </div>
        )}
      </div>

      {/* Portfolio Modal */}
      {showPortfolioModal && selectedCreatorPortfolio && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 md:p-4 overflow-y-auto overscroll-contain"
          onClick={() => setShowPortfolioModal(false)}
        >
          <div
            className="bg-white rounded-2xl border border-gray-200 shadow-lg max-w-4xl w-full max-h-[90vh] supports-[height:100dvh]:max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 md:p-6 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-2xl">
              <div className="flex justify-between items-start gap-3">
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#3EB489] rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg md:text-2xl font-bold text-[#0F172A] truncate">{selectedCreatorPortfolio.name}</h2>
                    <p className="text-sm text-[#475569] line-clamp-2">{selectedCreatorPortfolio.bio || 'Geen bio beschikbaar'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPortfolioModal(false)}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition flex-shrink-0"
                >
                  <X className="w-6 h-6 text-[#0F172A]" />
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6">
              {selectedCreatorPortfolio.portfolio && selectedCreatorPortfolio.portfolio.length > 0 ? (
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-[#0F172A] mb-4 md:mb-6">Portfolio ({selectedCreatorPortfolio.portfolio.length} items)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {selectedCreatorPortfolio.portfolio.map((item, index) => {
                      const embed = getPortfolioEmbed(item)
                      const isUpload = embed.kind === 'file'
                      return (
                      <div key={index} className="relative group">
                        <div className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-md">

                          {/* Alles speelt op het platform af. Eerder opende een niet-upload
                              in een nieuw tabblad op YouTube, waardoor het bedrijf van
                              UGC4you af werd geleid en de creator daar rechtstreeks kon
                              benaderen. YouTube wordt nu ingesloten; overige externe
                              bronnen tonen alleen een thumbnail, zonder uitgaande link. */}
                          <div className="aspect-video bg-gray-900 relative">
                            <PortfolioVideo item={item} objectFit="contain" />

                            {/* Video Type Indicator */}
                            <div className="absolute top-3 right-3 pointer-events-none">
                              <span className={`px-2 py-1 ${isUpload ? 'bg-green-500/80' : 'bg-blue-500/80'} rounded-lg text-xs font-medium text-white`}>
                                {isUpload ? 'Geüpload' : 'Extern'}
                              </span>
                            </div>

                            {/* Category Badge */}
                            {item.category && (
                              <div className="absolute top-3 left-3 pointer-events-none">
                                <span className="px-2 py-1 bg-black/50 rounded-lg text-xs font-medium text-white">
                                  {item.category}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-3 md:p-4">
                            <h4 className="font-semibold text-[#0F172A] mb-1.5 line-clamp-2">{item.title}</h4>
                            {item.description && (
                              <p className="text-[#475569] text-sm line-clamp-3">{item.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-[#475569]/50" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">Geen portfolio items</h3>
                  <p className="text-[#475569]">
                    Deze creator heeft nog geen portfolio items toegevoegd.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Accepteer-bevestiging */}
      {acceptProposal && (() => {
        // De aanbetaling die Stripe int = depositPercent van het GEBODEN bedrag: de backend
        // legt bij accepteren het bod vast als agreedAmount en herinitialiseert de betaling
        // (zie routes/proposals.js accept + Project.initializePayments). Reken hier dus met
        // het bod; alleen zonder bod geldt het projectbudget. Zelfde afronding als backend:
        // round2 per stap, zodat de modal exact noemt wat Stripe straks afschrijft.
        const depositPercent = project?.payment?.depositPercent ?? 25
        const remainderPercent = 100 - depositPercent
        const round2 = (n) => Math.round(n * 100) / 100
        const bidAmount = getProposalAmount(acceptProposal)
        const baseExcl = bidAmount || project?.agreedAmount || project?.budget || 0
        const depositExcl = round2(baseExcl * (depositPercent / 100))
        const depositIncBtw = round2(depositExcl + round2(depositExcl * 0.21)).toFixed(2)
        const totalIncBtw = round2(baseExcl + round2(baseExcl * 0.21)).toFixed(2)
        const creatorName = acceptProposal.creator?.name || 'deze creator'
        const firstName = creatorName.split(' ')[0]

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto overscroll-contain">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl border border-gray-200 shadow-lg max-w-lg w-full max-h-[90vh] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">

              {/* Header — sticky zodat de sluitknop bij scrollen in beeld blijft */}
              <div className="p-5 sm:p-6 md:p-8 pb-4 md:pb-4 flex items-start justify-between gap-3 sticky top-0 bg-white z-10 rounded-t-2xl">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden flex-shrink-0" style={{ background: acceptProposal.creator?.profilePictureUrl ? 'transparent' : '#3EB489', display: 'grid', placeItems: 'center' }}>
                    {acceptProposal.creator?.profilePictureUrl ? (
                      <img src={acceptProposal.creator.profilePictureUrl} alt={creatorName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-bold text-white">{creatorName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold text-[#0F172A] break-words">Reserveer {firstName}</h2>
                    <p className="text-sm text-[#475569] mt-1">Je legt deze creator vast voor je opdracht</p>
                  </div>
                </div>
                <button onClick={() => !accepting && setAcceptProposal(null)} className="p-2.5 -mr-1 hover:bg-gray-100 rounded-lg transition flex-shrink-0" title="Sluiten">
                  <X className="w-5 h-5 text-[#475569]" />
                </button>
              </div>

              <div className="p-5 sm:p-6 md:p-8 pt-2 sm:pt-2 md:pt-4">
                {/* Betaaloverzicht */}
                <div className="rounded-xl border border-gray-200 overflow-hidden mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 px-4 sm:px-5 py-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A]">{bidAmount ? `Geboden prijs van ${firstName}` : 'Projectbudget'}</p>
                      <p className="text-xs text-[#475569] mt-0.5">&euro;{baseExcl} excl. BTW — dit wordt het afgesproken bedrag</p>
                    </div>
                    <span className="text-sm font-medium text-[#475569] whitespace-nowrap self-end sm:self-auto">&euro;{totalIncBtw} incl. BTW</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 px-4 sm:px-5 py-4 border-t border-gray-100">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A]">Aanbetaling nu ({depositPercent}%)</p>
                      <p className="text-xs text-[#475569] mt-0.5">Veilig in escrow tot jij de content goedkeurt</p>
                    </div>
                    <span className="text-lg font-bold text-[#0F172A] whitespace-nowrap self-end sm:self-auto">&euro;{depositIncBtw} <span className="text-xs font-medium text-[#475569]">incl. BTW</span></span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 px-4 sm:px-5 py-4 border-t border-gray-100">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A]">Resterende {remainderPercent}%</p>
                      <p className="text-xs text-[#475569] mt-0.5">Pas na goedkeuring van de content</p>
                    </div>
                    <span className="text-sm font-medium text-[#475569] whitespace-nowrap self-end sm:self-auto">later</span>
                  </div>
                </div>

                {/* Geruststelling */}
                <div className="space-y-5 mb-8">
                  <div className="flex gap-4">
                    <ShieldCheck className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">Niet goed, geld terug</p>
                      <p className="text-sm text-[#475569] mt-1">Je aanbetaling gaat pas naar {firstName} nadat jij de content hebt goedgekeurd. Niet tevreden? Dan krijg je je geld terug.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Clock className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">Rustig beslissen</p>
                      <p className="text-sm text-[#475569] mt-1">Je hebt 72 uur voor de aanbetaling. Kom je er niet aan toe, dan vervalt de reservering vanzelf — zonder kosten.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Users className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">Meerdere creators?</p>
                      <p className="text-sm text-[#475569] mt-1">Je kunt hierna nog meer voorstellen accepteren. Elke creator krijgt een eigen workspace en eigen aanbetaling op basis van zijn of haar voorstel.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">Direct aan de slag</p>
                      <p className="text-sm text-[#475569] mt-1">Na de aanbetaling opent jullie gezamenlijke workspace en gaat {firstName} voor je aan het werk.</p>
                    </div>
                  </div>
                </div>

                {/* Acties */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setAcceptProposal(null)}
                    disabled={accepting}
                    className="flex-1 px-6 py-3 rounded-xl font-semibold border border-gray-200 text-[#475569] hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    Nog even niet
                  </button>
                  <button
                    onClick={confirmAcceptProposal}
                    disabled={accepting}
                    className="flex-1 px-6 py-3 min-h-[48px] rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition inline-flex items-center justify-center disabled:opacity-60"
                  >
                    {accepting ? 'Even geduld…' : 'Reserveer & ga naar betaling'}
                  </button>
                </div>
                <p className="text-xs text-[#94A3B8] text-center mt-4">🔒 Veilig betalen via Stripe · iDEAL, creditcard en meer</p>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Chat Dialog */}
      <ChatDialog
        isOpen={showChatDialog}
        onClose={() => {
          setShowChatDialog(false)
          setSelectedChatCreator(null)
        }}
        creator={selectedChatCreator}
        project={project}
      />
    </div>
  )
}

export default ProjectProposalsPage